import { ReactNode } from 'react';

import Breadcrumbs from '@/components/Breadcrumbs';
import { ResourceType } from '@/types/schema';

import { DetailPageTitle } from '../PageTitle';
import {
  ResourceActionsSlot,
  ResourceTabsSlot,
} from './ResourceTabLayoutPortals';
import { useResourceLayoutMeasurement } from './useResourceLayoutMeasurement';

type PageTitleProps = {
  title: string;
  id?: string;
  type: ResourceType;
  description?: ReactNode;
  isLoading?: boolean;
  leftDecorator?: ReactNode;
  rightDecorator?: ReactNode;
};

export const ResourceDetailTabLayout: React.FC<
  React.PropsWithChildren<PageTitleProps>
> = ({
  title,
  id,
  type,
  description,
  children,
  isLoading,
  leftDecorator,
  rightDecorator,
}) => {
  const { containerRef, tabsRef, actionsRef, titleMaxWidth } =
    useResourceLayoutMeasurement();

  return (
    <ResourceTabsSlot.Context>
      <ResourceActionsSlot.Context>
        <div
          className="flex items-start border-b border-b-secondary px-4 pt-6 @container"
          ref={containerRef}
        >
          <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
            <DetailPageTitle
              title={title}
              id={id}
              resourceType={type}
              description={description}
              isLoading={isLoading}
              leftDecorator={leftDecorator}
              rightDecorator={rightDecorator}
              maxWidth={titleMaxWidth}
            />

            <div ref={tabsRef}>
              <ResourceTabsSlot.Slot className="-mb-[1px] pt-0.5" />
            </div>
          </div>

          <div className="flex-grow" />

          <div ref={actionsRef}>
            <ResourceActionsSlot.Slot className="-mt-2" />
          </div>
        </div>

        {children}
      </ResourceActionsSlot.Context>
    </ResourceTabsSlot.Context>
  );
};

export const ResourceDetailLayout: React.FC<
  React.PropsWithChildren<{
    pageTitleProps: PageTitleProps;
    hideBreadcrumbs?: boolean;
  }>
> = ({ pageTitleProps, children, hideBreadcrumbs }) => {
  return (
    <div className="flex h-screen flex-1 flex-col">
      {!hideBreadcrumbs && (
        <div className="px-4 pt-3">
          <Breadcrumbs />
        </div>
      )}
      <div className="flex flex-grow flex-col overflow-hidden">
        <ResourceDetailTabLayout {...pageTitleProps}>
          {children}
        </ResourceDetailTabLayout>
      </div>
    </div>
  );
};
